import { Stack } from '@solvhealth/jigsaw';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import OtpCodeLoginForm from '../../components/OtpCodeLoginForm/OtpCodeLoginForm';
import PhoneLoginForm from '../../components/PhoneLoginForm/PhoneLoginForm';
import { LOGIN_ACTION_OTP_FORM } from '../../constants/index';
import { analyticsTrackEvent } from '../../core/analytics';
import { LOGIN_PAGE_LOAD } from '../../core/analytics/events';
import history from '../../core/history';
import { isEmptyArray } from '../../core/util/array';
import { formatLocationName, getLocationWithHardCodedLogoAndName } from '../../core/util/location';
import { isEmptyObject } from '../../core/util/object';
import { getCurrentPath } from '../../core/util/url';
import useSetTitle from '../../hooks/useSetTitle';
import ClinicLogo from '../Location/ClinicLogo';

const title = 'Login';

const ClinicLogoContainer = styled(ClinicLogo)`
  margin: 36px auto;
  text-align: center;
  & img {
    height: auto;
    max-width: 300px;
    max-height: 120px;
  }
`;

type Props = {
  bookingId?: string;
  login?: any;
  onLoginSuccess?: (...args: any[]) => any;
  isBookingWidgetEmbedded?: boolean;
  invoiceId?: string;
  resetLoginData?: (...args: any[]) => any;
  fetchLocationDataFromInvoice?: (...args: any[]) => any;
  location: any;
  locationId?: string;
  groupId?: string;
  saveNewInvoiceSource?: (...args: any[]) => any;
  groupLocations?: any;
  fetchGroupLocations?: (...args: any[]) => any;
  shouldCreateAccountIfNotFound?: boolean;
  redirectToUrl?: string;
  isLoginModalOpen?: boolean;
  query?: any;
  isModal?: boolean;
  hideLoginModal?: (...args: any[]) => any;
  cookies?: any;
};
/**
 *
 */
function LoginForm(props: Props) {
  const {
    invoiceId,
    fetchLocationDataFromInvoice,
    locationId,
    groupId,
    resetLoginData,
    isBookingWidgetEmbedded,
    saveNewInvoiceSource,
    groupLocations,
    fetchGroupLocations,
    isModal,
    hideLoginModal,
    location,
    login,
  } = props;

  useSetTitle(title);

  useEffect(() => (locationId || groupId) && saveNewInvoiceSource?.({ locationId, groupId }), [
    groupId,
    locationId,
    saveNewInvoiceSource,
  ]);
  useEffect(() => analyticsTrackEvent(LOGIN_PAGE_LOAD, { loginPath: getCurrentPath() }), []);
  useEffect(
    () => invoiceId && fetchLocationDataFromInvoice && fetchLocationDataFromInvoice(invoiceId),
    [invoiceId, fetchLocationDataFromInvoice]
  );

  const [areGroupLocationsStale, setAreGroupLocationsStale] = useState(true);
  useEffect(() => {
    if (areGroupLocationsStale && groupId) {
      fetchGroupLocations?.(groupId);
      setAreGroupLocationsStale(false);
    }
  }, [fetchGroupLocations, groupId, areGroupLocationsStale]);

  const handleCloseModal = useCallback(() => {
    if (!isModal) {
      if (login.action === LOGIN_ACTION_OTP_FORM) {
        return resetLoginData?.();
      }
      return history.push('/');
    }

    return hideLoginModal?.();
  }, [isModal, hideLoginModal, resetLoginData, login.action]);

  const fixedLocation = useMemo(() => {
    if (
      invoiceId &&
      !isEmptyObject(location) &&
      (location.id === 'JA68Mg' || location.id === 'AzWY8p')
    ) {
      return getLocationWithHardCodedLogoAndName(location);
    }

    if (!isEmptyObject(groupLocations) && !isEmptyArray(groupLocations.locations)) {
      return groupLocations.locations[0];
    }

    return location;
  }, [groupLocations, invoiceId, location]);

  const header = useMemo(() => {
    if (!isBookingWidgetEmbedded && (invoiceId || groupId)) {
      return (
        <ClinicLogoContainer alt={formatLocationName(fixedLocation)} location={fixedLocation} />
      );
    }
    if (isModal) return null;

    return null;
  }, [fixedLocation, groupId, invoiceId, isBookingWidgetEmbedded, isModal]);

  const hasOTPBeenSent = login.login && login.login.otp_sent;

  const propsWithFixedLocation = { ...props, location: fixedLocation };

  return (
    <>
      {header}

      <Stack width="100%">
        {hasOTPBeenSent ? (
          <OtpCodeLoginForm {...props} />
        ) : (
          <PhoneLoginForm {...propsWithFixedLocation} />
        )}
      </Stack>
    </>
  );
}

LoginForm.contextTypes = {
  setTitle: PropTypes.func.isRequired,
};

export default LoginForm;
